/**
 * 帳款報表分頁(REQ-20260815002)—— 舊 PC 會計系統「應收付款」選單裡那一疊表。
 *
 * 應收 / 應付各一個分頁,底下用下拉挑要看哪一張,選了就在同一個掛載點顯示。
 * 做成「一個分頁 + 下拉」而不是「一張報表一個分頁」:九張報表攤平成九個分頁會把
 * 既有的四個分頁擠爆,而且這些報表本來就是同一類東西(全公司帳款的不同切法)。
 *
 * 每張報表的欄位定義與計算公式在後端(`ar-ap-reports.service.ts`)與
 * `@app/shared` 的 `ar-ap-report.schema.ts` 檔頭,這裡只負責挑選、篩選與呈現。
 */
import { useEffect, useRef, useState } from 'react';
import type { MailingLabelRow, PartyInfo } from '@app/shared';
import { ExportJpegButton } from '../../components/ExportJpegButton';
import { PartyPicker } from '../../components/PartyPicker';
import { formatMoney } from '../../lib/format-money';
import { useSalesPersons } from '../accounting/api';
import { ArDetailItemTable, type ArDetailPrintHeaderInfo } from './ArDetailItemTable';
import {
  GroupedArDetailStatements,
  type GroupedArDetailHeaderInfo,
} from './GroupedArDetailStatements';
import { GroupedCustomerStatements } from './GroupedCustomerStatements';
import { ReportTable, type ReportColumn } from './ReportTable';
import { buildReportFilename } from './report-filename';
import {
  useApDetailReport,
  useApMonthlyStats,
  useApSettlementDaily,
  useApSummaryReport,
  useArAging,
  useArDetailReport,
  useArMailingLabels,
  useArMonthlyStats,
  useArOverview,
  useArSalesPersonSummary,
  useArSettlementDaily,
  useArSummaryReport,
} from './api';

/** 每張報表要哪些篩選器 —— 決定上方那排控制項顯示什麼 */
type FilterKind = 'range' | 'range+person' | 'date';

interface ReportDef {
  key: string;
  label: string;
  filter: FilterKind;
  /** 下拉旁邊的一句話說明,因為「簡要 / 彙總 / 統計」這幾個名字很容易混淆 */
  hint: string;
}

const AR_REPORTS: ReportDef[] = [
  {
    key: 'ar-detail',
    label: '應收帳款明細表',
    filter: 'range',
    hint: '一列一個品項:商品 / 數量 / 單位 / 單價 / 金額,加上該單的折讓 / 已收款 / 未收款。建議先選客戶(表上已不印客戶欄)。',
  },
  {
    key: 'ar-summary',
    label: '應收簡要表',
    filter: 'range',
    hint: '明細表以客戶為單位彙總,只列還有未結餘額的客戶。',
  },
  {
    key: 'ar-by-person',
    label: '業務員應收帳款明細',
    filter: 'range+person',
    hint: '同明細表,但依業務員分組;可只看單一業務員。',
  },
  {
    key: 'ar-person-summary',
    label: '業務員應收統計報表',
    filter: 'range',
    hint: '每個業務員的未結合計與單據張數。',
  },
  {
    key: 'ar-monthly',
    label: '應收帳款統計表',
    filter: 'range',
    hint: '一列一個月份:本期出貨 / 折讓 / 收款的發生額(跨全體客戶,不看餘額)。',
  },
  {
    key: 'ar-daily',
    label: '收款沖銷日報表',
    filter: 'date',
    hint: '指定那一天登錄的收款沖帳紀錄。',
  },
  {
    key: 'ar-overview',
    label: '應收帳款彙總表',
    filter: 'range',
    hint: '一列一個客戶:期初 → 本期出貨 / 折讓 / 收款 → 期末的滾動過程。',
  },
  {
    key: 'ar-aging',
    label: '應收帳款帳齡分析',
    filter: 'range',
    hint: '未結金額依逾期天數分桶(到期日缺漏時用單據日期)。',
  },
  {
    key: 'ar-labels',
    label: '應收帳款郵寄標籤',
    filter: 'range',
    hint: '有未結應收的客戶名條,可直接列印。',
  },
];

const AP_REPORTS: ReportDef[] = [
  {
    key: 'ap-detail',
    label: '應付帳款明細表',
    filter: 'range',
    hint: '一列一張還沒付完的進貨單,含廠商名稱。',
  },
  {
    key: 'ap-summary',
    label: '應付簡要表',
    filter: 'range',
    hint: '明細表以廠商為單位彙總,只列還有未結餘額的廠商。',
  },
  {
    key: 'ap-monthly',
    label: '應付帳款統計表',
    filter: 'range',
    hint: '一列一個月份:本期進貨 / 折讓 / 付款的發生額(跨全體廠商)。',
  },
  {
    key: 'ap-daily',
    label: '付款沖銷日報表',
    filter: 'date',
    hint: '指定那一天登錄的付款沖帳紀錄。',
  },
];

export function ReportsPanel({ side }: { side: 'ar' | 'ap' }): JSX.Element {
  const defs = side === 'ar' ? AR_REPORTS : AP_REPORTS;
  const [reportKey, setReportKey] = useState(defs[0]?.key ?? '');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [date, setDate] = useState(todayInTaipei);
  const [salesPersonId, setSalesPersonId] = useState<number | null>(null);
  /**
   * 應收明細表可再篩單一客戶(REQ-20260824010)。留白 = 全部客戶,
   * 與 `from` / `to` 同一條約定;日期也留白時就是「這位客戶目前全部未結單據」。
   *
   * 換報表時**不清掉**,與 `from` / `to` / `salesPersonId` 一致 ——
   * 這個篩選只在 `ar-detail` 生效,而挑選器也只在 `ar-detail` 顯示,
   * 兩者永遠同時在畫面上(選定後 `PartyPicker` 會印出客戶名稱與編號),
   * 不會出現「查詢結果莫名變少但看不到原因」的狀態。
   *
   * 選定客戶還會鎖住「依客戶分別列印」(REQ-20260831005,見 `lockPrintByCustomer`)。
   */
  const [customer, setCustomer] = useState<PartyInfo | null>(null);
  /**
   * 應收明細的兩張表可切成「一位客戶一份」的請款版面(REQ-20260815012)。
   * 帳款明細表已指定單一客戶時這個切換會被鎖住(REQ-20260831005,見 `lockPrintByCustomer`)。
   */
  const [printByCustomer, setPrintByCustomer] = useState(false);

  const def = defs.find((d) => d.key === reportKey) ?? defs[0];
  const range = { from: from || undefined, to: to || undefined };
  const { data: salesPersons } = useSalesPersons();

  /**
   * 每張報表都掛一個 query,靠 `enabled` 只讓選中的那張去打 API ——
   * hooks 不能寫在條件式裡,而 `enabled: false` 的 query 不會發出請求。
   */
  const on = (key: string): boolean => reportKey === key;
  /**
   * 帳款明細表已經篩到單一客戶時,「依客戶分別列印」鎖住且不生效(REQ-20260831005)——
   * 分組只會分出一組,而使用者指名要鎖住。用衍生值而不是去 `setPrintByCustomer(false)`:
   * 清掉客戶後使用者原本的勾選狀態自然回來。
   *
   * ⚠️ REQ-20260831005 §二 當時有第二個理由「勾了就會失去 REQ-20260831004 的完整表頭」,
   * **REQ-20260901013 之後不再成立** —— 分組版面現在每位客戶各有一份一模一樣的完整
   * 抬頭與合計區。這個鎖現在只剩「使用者指名要鎖住」這一個理由撐著。
   */
  const lockPrintByCustomer = on('ar-detail') && customer != null;
  const groupedForArDetail = printByCustomer && !lockPrintByCustomer;
  /**
   * 帳款明細表未勾分組列印時,列印抬頭換成一份完整表頭(REQ-20260831004)。
   * 勾了分組列印就交給 `GroupedArDetailStatements`,那邊**每位客戶各印一份同樣的
   * 完整抬頭**(REQ-20260901013)。判斷取**實際生效值**,所以鎖住時即使 state
   * 殘留 true 仍走這裡的完整表頭。
   */
  const arDetailFullHeader = on('ar-detail') && !groupedForArDetail;
  const arDetail = useArDetailReport(range, undefined, customer?.id, on('ar-detail'));
  /**
   * 勾分組列印時,`GroupedArDetailStatements` 讓每位客戶各帶一份完整抬頭
   * (含報表名稱),所以 `.print-area` 那行共用的 `h3` 也要讓開(REQ-20260901013)。
   *
   * `some()` 的條件必須與 `ArDetailView` 的 `rows` 過濾一致 —— 一列可請款的都沒有時
   * 會落回 `ArDetailItemTable`(那時沒有任何抬頭),h3 就得留著,
   * 否則印出來是一張沒有標題的空表。
   */
  const arDetailGroupedHeader =
    groupedForArDetail && (arDetail.data?.rows ?? []).some((r) => r.delivered !== false);
  const arByPerson = useArDetailReport(
    range,
    salesPersonId ?? undefined,
    undefined,
    on('ar-by-person'),
  );
  const arSummary = useArSummaryReport(range, on('ar-summary'));
  const arPersonSummary = useArSalesPersonSummary(range, on('ar-person-summary'));
  const arMonthly = useArMonthlyStats(range, on('ar-monthly'));
  const arDaily = useArSettlementDaily(date, on('ar-daily'));
  const arOverview = useArOverview(range, on('ar-overview'));
  const arAging = useArAging(range, on('ar-aging'));
  const arLabels = useArMailingLabels(range, on('ar-labels'));
  const apDetail = useApDetailReport(range, on('ap-detail'));
  const apSummary = useApSummaryReport(range, on('ap-summary'));
  const apMonthly = useApMonthlyStats(range, on('ap-monthly'));
  const apDaily = useApSettlementDaily(date, on('ap-daily'));

  /**
   * 「列印 / 匯出 PDF」的預設檔名(REQ-20260831003)—— 存檔對話框帶出來的就是
   * 分頁標題,所以在這裡設 `document.title`,離開時還原成進來之前的值
   * (與 REQ-20260811002 的訂單列印頁同一招,不引 PDF 套件)。
   *
   * 客戶那一段只有挑選器真的顯示時才產生,判斷沿用 `on('ar-detail')`,
   * 免得畫面上沒有客戶條件、檔名卻寫著客戶。
   */
  const filename = buildReportFilename({
    reportLabel: def?.label ?? '',
    filter: def?.filter ?? 'range',
    hasCustomerFilter: on('ar-detail'),
    customer,
    from,
    to,
    date,
  });
  useEffect(() => {
    const previousTitle = document.title;
    document.title = filename;
    return () => {
      document.title = previousTitle;
    };
  }, [filename]);

  /**
   * 「另存 JPEG」要截的那一塊(REQ-20260903006)。**檔名沿用 `buildReportFilename()`
   * 的結果**,不改成單據頁那套連字號格式 —— 這套 `報表名稱_客戶簡稱_日期區間`
   * 是使用者在 REQ-20260831003 / REQ-20260901001 驗收過的。
   */
  const sheetRef = useRef<HTMLDivElement>(null);

  return (
    <div className="grid gap-4" ref={sheetRef}>
      <div className="grid gap-3 md:grid-cols-4 no-print">
        <div className="grid gap-1 md:col-span-2">
          <label className="text-sm text-slate-600" htmlFor="report-picker">
            報表
          </label>
          <select
            id="report-picker"
            data-testid="report-picker"
            value={reportKey}
            onChange={(e) => setReportKey(e.target.value)}
            className="border rounded-md px-3 py-2 text-sm"
          >
            {defs.map((d) => (
              <option key={d.key} value={d.key}>
                {d.label}
              </option>
            ))}
          </select>
        </div>

        {def?.filter === 'date' ? (
          <div className="grid gap-1">
            <label className="text-sm text-slate-600" htmlFor="report-date">
              日期
            </label>
            <input
              id="report-date"
              type="date"
              data-testid="report-date"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              className="border rounded-md px-3 py-2 text-sm"
            />
          </div>
        ) : (
          <>
            <div className="grid gap-1">
              <label className="text-sm text-slate-600" htmlFor="report-from">
                起日(可留白)
              </label>
              <input
                id="report-from"
                type="date"
                data-testid="report-from"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
                className="border rounded-md px-3 py-2 text-sm"
              />
            </div>
            <div className="grid gap-1">
              <label className="text-sm text-slate-600" htmlFor="report-to">
                迄日(含當天)
              </label>
              <input
                id="report-to"
                type="date"
                data-testid="report-to"
                value={to}
                onChange={(e) => setTo(e.target.value)}
                className="border rounded-md px-3 py-2 text-sm"
              />
            </div>
            {/*
              帳款明細表的區間篩的是**完貨交貨日**而不是訂單日期(REQ-20260901008)——
              同一組起訖日在這張表上的意思跟其他八張不一樣,不寫出來使用者會以為
              「這個月下的單怎麼少了幾張」。**只是一句說明,不是開關** ——
              認列依據沒有第二種選項可選(CLAUDE.md §2)。
            */}
            {on('ar-detail') ? (
              <p className="text-xs text-slate-500 md:col-span-4" data-testid="ar-detail-date-note">
                此表以完貨交貨日期認列，未交貨的單不列入列印。
              </p>
            ) : null}
          </>
        )}

        {def?.filter === 'range+person' ? (
          <div className="grid gap-1">
            <label className="text-sm text-slate-600" htmlFor="report-sales-person">
              業務員
            </label>
            <select
              id="report-sales-person"
              data-testid="report-sales-person"
              value={salesPersonId ?? ''}
              onChange={(e) => setSalesPersonId(e.target.value === '' ? null : Number(e.target.value))}
              className="border rounded-md px-3 py-2 text-sm"
            >
              <option value="">全部業務員</option>
              {(salesPersons?.items ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.displayName}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </div>

      {/*
        單一客戶的篩選(REQ-20260824010)—— 只有應收帳款明細表有,需求指名的是這一張。
        另起一列而不是擠進上面那個 4 欄 grid:挑選器選定後會展開統編 / 地址那幾格,
        塞在 1/4 欄寬裡會把起訖日那排壓壞。
      */}
      {on('ar-detail') ? (
        <div className="grid gap-3 md:grid-cols-2 no-print" data-testid="report-customer">
          <PartyPicker type="customer" value={customer} onChange={setCustomer} />
        </div>
      ) : null}

      <div className="flex items-start justify-between gap-4 no-print">
        <p className="text-xs text-slate-500">{def?.hint}</p>
        <div className="flex shrink-0 items-center gap-3">
          {on('ar-detail') || on('ar-by-person') ? (
            <label
              className={`inline-flex items-center gap-1.5 text-sm ${
                lockPrintByCustomer ? 'text-slate-400' : 'text-slate-600'
              }`}
            >
              <input
                type="checkbox"
                data-testid="print-by-customer"
                disabled={lockPrintByCustomer}
                checked={on('ar-detail') ? groupedForArDetail : printByCustomer}
                onChange={(e) => setPrintByCustomer(e.target.checked)}
              />
              依客戶分別列印
              {lockPrintByCustomer ? (
                <span className="text-xs text-slate-400">已指定單一客戶,不需分別列印</span>
              ) : null}
            </label>
          ) : null}
          {/* 同一份報表的第二種存檔格式(REQ-20260903006);PDF 那條路一行未動 */}
          <ExportJpegButton targetRef={sheetRef} filename={filename} />
          <button
            type="button"
            onClick={() => window.print()}
            className="px-3 py-1.5 rounded border hover:bg-slate-50 text-sm"
          >
            列印 / 匯出 PDF
          </button>
        </div>
      </div>

      <div className="print-area">
        {/*
          帳款明細表(未勾分組列印)改用一份完整表頭(REQ-20260831004)——
          公司抬頭 / 報表名稱 / 雙方基本資料 / 帳款區間都在那裡面印一次,
          所以這裡的共用 h3 與 REQ-20260831002 的客戶小字要一起讓開,
          否則報表名稱與客戶會在同一頁印兩次(那正是使用者標「刪除」的中段區塊)。

          抬頭本身**不在這裡渲染**(REQ-20260901002):使用者要每一頁最上面都有抬頭,
          所以它跟著 `ArDetailPrintPages` 的每一個頁面區塊各印一次,
          印在這裡只會變成第一頁多一份。

          勾了分組列印時也一樣讓開(REQ-20260901013):那個版面現在**每位客戶各有一份
          完整抬頭**,共用的 h3 只會讓報表名稱在第一頁印兩次。其餘八張報表與
          `ar-by-person` 仍走底下這行共用的 h3 —— 置中是報表標題的通則
          (REQ-20260817003),不為單張分岔。

          「勾了分組列印」看的是實際生效值:已指定單一客戶時分組被鎖住
          (REQ-20260831005),所以那時走的仍是完整表頭。
        */}
        {arDetailFullHeader || arDetailGroupedHeader ? null : (
          <>
            <h3 className="hidden print:block text-center text-base font-medium mb-2">
              {def?.label}
            </h3>
            {/*
              帳款明細表拿掉了客戶欄(REQ-20260831002),印出來會失去客戶身分 ——
              選定客戶時把它補在標題底下。畫面上挑選器就在旁邊,所以只在列印時顯示。
            */}
            {on('ar-detail') && customer ? (
              <p className="hidden print:block text-center text-xs text-slate-600 mb-2">
                客戶：{customer.simpleName}
                {customer.code ? `（${customer.code}）` : ''}
              </p>
            ) : null}
          </>
        )}

        {on('ar-detail') ? (
          <ArDetailView
            q={arDetail}
            grouped={groupedForArDetail}
            itemLevel
            printHeader={
              arDetailFullHeader
                ? { reportLabel: def?.label ?? '', customer, from, to }
                : undefined
            }
            groupedHeader={{ reportLabel: def?.label ?? '', from, to }}
          />
        ) : null}
        {on('ar-by-person') ? <ArDetailView q={arByPerson} grouped={printByCustomer} /> : null}
        {on('ap-detail') ? <DetailTable q={apDetail} partyLabel="廠商" /> : null}
        {on('ar-summary') ? <SummaryTable q={arSummary} partyLabel="客戶" /> : null}
        {on('ap-summary') ? <SummaryTable q={apSummary} partyLabel="廠商" /> : null}
        {on('ar-person-summary') ? <SalesPersonSummaryTable q={arPersonSummary} /> : null}
        {on('ar-monthly') ? <MonthlyTable q={arMonthly} amountLabel="本期出貨" settledLabel="本期收款" /> : null}
        {on('ap-monthly') ? <MonthlyTable q={apMonthly} amountLabel="本期進貨" settledLabel="本期付款" /> : null}
        {on('ar-daily') ? (
          <DailyTable q={arDaily} partyLabel="客戶" kindLabel="收款" showTransfer />
        ) : null}
        {on('ap-daily') ? <DailyTable q={apDaily} partyLabel="廠商" kindLabel="付款" /> : null}
        {on('ar-overview') ? <OverviewTable q={arOverview} /> : null}
        {on('ar-aging') ? <AgingTable q={arAging} /> : null}
        {on('ar-labels') ? <MailingLabels q={arLabels} /> : null}
      </div>
    </div>
  );
}

// ──────────────────────────────────────────────────────────────────────────
// 各報表的欄位定義
// ──────────────────────────────────────────────────────────────────────────

/** `useQuery` 的回傳裡報表只用得到這四個欄位 —— 收窄成自己的型別,少一層泛型雜訊 */
interface Q<T> {
  data: T | undefined;
  isLoading: boolean;
  isError: boolean;
  error: unknown;
}

type DetailReport = NonNullable<ReturnType<typeof useArDetailReport>['data']>;
type DetailRow = DetailReport['rows'][number];

/**
 * 應收明細的三種呈現。勾了「依客戶分別列印」而且資料已經回來,就換成一位客戶一份的
 * 請款版面(REQ-20260815012);其餘情況(載入中 / 失敗 / 沒資料)走表格 ——
 * 三態的訊息只留在表格一處,不在分組版面再寫一份。
 * 三者吃的是同一份 query 結果,切換不會再打一次 API。
 *
 * `itemLevel` 由呼叫端指定,不在這裡猜 report key:
 *  - **帳款明細表**(`ar-detail`)走品項層級的 `ArDetailItemTable`(REQ-20260831002)
 *  - **業務員應收帳款明細**(`ar-by-person`)維持原本的 `DetailTable` ——
 *    那張沒有客戶篩選器,拿掉客戶欄會讓它無法使用
 *
 * 分組版面走哪一支也由呼叫端決定,一樣不猜 report key(REQ-20260901013):
 * 有 `groupedHeader` 就走與單一客戶版一致的 `GroupedArDetailStatements`
 * (`ar-detail`),沒有就維持原本的 `GroupedCustomerStatements`(`ar-by-person`)。
 */
function ArDetailView({
  q,
  grouped,
  itemLevel = false,
  printHeader,
  groupedHeader,
}: {
  q: Q<DetailReport>;
  grouped: boolean;
  itemLevel?: boolean;
  /** 帳款明細表未勾分組列印時的列印抬頭資料(REQ-20260901002);其餘報表不傳 */
  printHeader?: ArDetailPrintHeaderInfo | undefined;
  /**
   * 勾分組列印時每位客戶抬頭要的報表名稱與帳款區間(REQ-20260901013)。
   * **客戶那一格不在這裡** —— 每位客戶各自從自己的明細列上取。
   * 不傳就是 `ar-by-person`,維持原本的分組版面。
   */
  groupedHeader?: GroupedArDetailHeaderInfo | undefined;
}): JSX.Element {
  /*
   * 「依客戶分別列印」是拿去請款的版面,所以**只吃已交貨的列**(REQ-20260901008)。
   * 判斷用 `!== false`:`delivered` 是選填欄位,只有 `ar-detail` 會帶 ——
   * 業務員應收帳款明細(`ar-by-person`)沒帶,行為與改動前一字不差。
   * 全都還沒交貨時就落到底下的 `ArDetailItemTable`,由它的未交貨區交代原因,
   * 不是給一張空白的請款單。
   */
  const rows = (q.data?.rows ?? []).filter((r) => r.delivered !== false);
  if (grouped && rows.length > 0) {
    return groupedHeader ? (
      <GroupedArDetailStatements rows={rows} {...groupedHeader} />
    ) : (
      <GroupedCustomerStatements rows={rows} />
    );
  }
  if (itemLevel) return <ArDetailItemTable q={q} printHeader={printHeader} />;
  return <DetailTable q={q} partyLabel="客戶" withSalesPerson />;
}

function DetailTable({
  q,
  partyLabel,
  withSalesPerson = false,
}: {
  q: Q<DetailReport>;
  partyLabel: string;
  withSalesPerson?: boolean;
}): JSX.Element {
  const columns: Array<ReportColumn<DetailRow>> = [
    { key: 'txnDate', label: '日期', value: (r) => r.txnDate },
    { key: 'orderNo', label: '單號', value: (r) => r.orderNo, className: 'font-mono' },
    {
      key: 'partyName',
      label: partyLabel,
      value: (r) => r.partyName,
      render: (r) => (
        <span>
          {r.partyName}
          {r.partyCode ? (
            <span className="ml-1 text-slate-400 font-mono text-xs">{r.partyCode}</span>
          ) : null}
        </span>
      ),
    },
    ...(withSalesPerson
      ? [
          {
            key: 'salesPersonName',
            label: '業務員',
            value: (r: DetailRow) => r.salesPersonName ?? '未指定',
          },
        ]
      : []),
    { key: 'dueDate', label: '到期日', value: (r) => r.dueDate ?? '' , render: (r) => r.dueDate ?? '—' },
    { key: 'total', label: '含稅總計', align: 'right', value: (r) => Number(r.total), render: (r) => formatMoney(r.total) },
    {
      key: 'settledAmount',
      label: '已沖',
      align: 'right',
      value: (r) => Number(r.settledAmount),
      render: (r) => formatMoney(r.settledAmount),
    },
    {
      key: 'allowanceAmount',
      label: '折讓',
      align: 'right',
      value: (r) => Number(r.allowanceAmount),
      render: (r) => formatMoney(r.allowanceAmount),
    },
    {
      key: 'outstanding',
      label: '未結',
      align: 'right',
      value: (r) => Number(r.outstanding),
      render: (r) => formatMoney(r.outstanding),
    },
  ];

  return (
    <ReportTable
      columns={columns}
      rows={q.data?.rows ?? []}
      rowKey={(r) => r.orderId}
      isLoading={q.isLoading}
      isError={q.isError}
      error={q.error}
      emptyHint="這個區間內沒有未結的單據。到「出貨管理 / 進貨管理」建單並過帳後就會出現。"
      footer={[
        '合計',
        null,
        null,
        ...(withSalesPerson ? [null] : []),
        null,
        formatMoney(q.data?.totalAmount ?? '0'),
        formatMoney(q.data?.totalSettled ?? '0'),
        null,
        formatMoney(q.data?.totalOutstanding ?? '0'),
      ]}
    />
  );
}

type SummaryReport = NonNullable<ReturnType<typeof useArSummaryReport>['data']>;

function SummaryTable({
  q,
  partyLabel,
}: {
  q: Q<SummaryReport>;
  partyLabel: string;
}): JSX.Element {
  type Row = SummaryReport['rows'][number];
  const columns: Array<ReportColumn<Row>> = [
    { key: 'partyCode', label: '編號', value: (r) => r.partyCode ?? '', className: 'font-mono' },
    { key: 'partyName', label: partyLabel, value: (r) => r.partyName },
    { key: 'orderCount', label: '未結張數', align: 'right', value: (r) => r.orderCount },
    {
      key: 'totalAmount',
      label: '單據總額',
      align: 'right',
      value: (r) => Number(r.totalAmount),
      render: (r) => formatMoney(r.totalAmount),
    },
    {
      key: 'settledAmount',
      label: '已沖',
      align: 'right',
      value: (r) => Number(r.settledAmount),
      render: (r) => formatMoney(r.settledAmount),
    },
    {
      key: 'outstanding',
      label: '未結餘額',
      align: 'right',
      value: (r) => Number(r.outstanding),
      render: (r) => formatMoney(r.outstanding),
    },
  ];

  return (
    <ReportTable
      columns={columns}
      rows={q.data?.rows ?? []}
      rowKey={(r) => r.partyId}
      isLoading={q.isLoading}
      isError={q.isError}
      error={q.error}
      emptyHint={`目前沒有還有未結餘額的${partyLabel}。`}
      footer={[
        '合計',
        null,
        q.data?.rows.length ?? 0,
        formatMoney(q.data?.totalAmount ?? '0'),
        formatMoney(q.data?.totalSettled ?? '0'),
        formatMoney(q.data?.totalOutstanding ?? '0'),
      ]}
    />
  );
}

type PersonReport = NonNullable<ReturnType<typeof useArSalesPersonSummary>['data']>;

function SalesPersonSummaryTable({ q }: { q: Q<PersonReport> }): JSX.Element {
  type Row = PersonReport['rows'][number];
  const columns: Array<ReportColumn<Row>> = [
    { key: 'salesPersonName', label: '業務員', value: (r) => r.salesPersonName },
    { key: 'orderCount', label: '未結張數', align: 'right', value: (r) => r.orderCount },
    {
      key: 'totalAmount',
      label: '單據總額',
      align: 'right',
      value: (r) => Number(r.totalAmount),
      render: (r) => formatMoney(r.totalAmount),
    },
    {
      key: 'outstanding',
      label: '未結餘額',
      align: 'right',
      value: (r) => Number(r.outstanding),
      render: (r) => formatMoney(r.outstanding),
    },
  ];

  return (
    <ReportTable
      columns={columns}
      rows={q.data?.rows ?? []}
      rowKey={(r) => r.salesPersonId ?? 'none'}
      isLoading={q.isLoading}
      isError={q.isError}
      error={q.error}
      emptyHint="目前沒有未結的應收單據。出貨單上指定業務員後,這裡就會分組統計。"
      footer={[
        '合計',
        null,
        formatMoney(q.data?.totalAmount ?? '0'),
        formatMoney(q.data?.totalOutstanding ?? '0'),
      ]}
    />
  );
}

type MonthlyReport = NonNullable<ReturnType<typeof useArMonthlyStats>['data']>;

function MonthlyTable({
  q,
  amountLabel,
  settledLabel,
}: {
  q: Q<MonthlyReport>;
  amountLabel: string;
  settledLabel: string;
}): JSX.Element {
  type Row = MonthlyReport['rows'][number];
  const columns: Array<ReportColumn<Row>> = [
    { key: 'period', label: '月份', value: (r) => r.period, className: 'font-mono' },
    { key: 'orderCount', label: '單據張數', align: 'right', value: (r) => r.orderCount },
    {
      key: 'amount',
      label: amountLabel,
      align: 'right',
      value: (r) => Number(r.amount),
      render: (r) => formatMoney(r.amount),
    },
    {
      key: 'allowance',
      label: '本期折讓',
      align: 'right',
      value: (r) => Number(r.allowance),
      render: (r) => formatMoney(r.allowance),
    },
    {
      key: 'settled',
      label: settledLabel,
      align: 'right',
      value: (r) => Number(r.settled),
      render: (r) => formatMoney(r.settled),
    },
  ];

  return (
    <ReportTable
      columns={columns}
      rows={q.data?.rows ?? []}
      rowKey={(r) => r.period}
      isLoading={q.isLoading}
      isError={q.isError}
      error={q.error}
      emptyHint="這個區間內沒有已過帳的單據或沖帳紀錄。"
      footer={[
        '合計',
        null,
        formatMoney(q.data?.totalAmount ?? '0'),
        formatMoney(q.data?.totalAllowance ?? '0'),
        formatMoney(q.data?.totalSettled ?? '0'),
      ]}
    />
  );
}

type DailyReport = NonNullable<ReturnType<typeof useArSettlementDaily>['data']>;

function DailyTable({
  q,
  partyLabel,
  kindLabel,
  showTransfer,
}: {
  q: Q<DailyReport>;
  partyLabel: string;
  kindLabel: string;
  /** 收款側才有「匯款」(REQ-20260817006);付款側不傳,那一欄就不長出來 */
  showTransfer?: boolean;
}): JSX.Element {
  type Row = DailyReport['rows'][number];
  const columns: Array<ReportColumn<Row>> = [
    { key: 'documentNo', label: '沖帳單號', value: (r) => r.documentNo, className: 'font-mono' },
    { key: 'partyName', label: partyLabel, value: (r) => r.partyName },
    {
      key: 'sourceDocNos',
      label: '沖銷單號',
      sortable: false,
      value: (r) => r.sourceDocNos.join('、'),
      render: (r) => (
        <span className="font-mono text-xs">{r.sourceDocNos.join('、') || '—'}</span>
      ),
    },
    {
      key: 'cashAmount',
      label: '現金',
      align: 'right',
      value: (r) => Number(r.cashAmount ?? 0),
      render: (r) => (r.cashAmount ? formatMoney(r.cashAmount) : '—'),
    },
    {
      key: 'checkAmount',
      label: '支票',
      align: 'right',
      value: (r) => Number(r.checkAmount ?? 0),
      render: (r) => (r.checkAmount ? formatMoney(r.checkAmount) : '—'),
    },
    ...(showTransfer
      ? [
          {
            key: 'transferAmount',
            label: '匯款',
            align: 'right' as const,
            value: (r: Row) => Number(r.transferAmount ?? 0),
            render: (r: Row) => (r.transferAmount ? formatMoney(r.transferAmount) : '—'),
          },
        ]
      : []),
    {
      key: 'usePrepaid',
      label: '動用預付/預收',
      align: 'right',
      value: (r) => Number(r.usePrepaid ?? 0),
      render: (r) => (r.usePrepaid ? formatMoney(r.usePrepaid) : '—'),
    },
    {
      key: 'totalSettled',
      label: `${kindLabel}合計`,
      align: 'right',
      value: (r) => Number(r.totalSettled),
      render: (r) => formatMoney(r.totalSettled),
    },
  ];

  return (
    <ReportTable
      columns={columns}
      rows={q.data?.rows ?? []}
      rowKey={(r) => r.settlementId}
      isLoading={q.isLoading}
      isError={q.isError}
      error={q.error}
      emptyHint={`${q.data?.date ?? '這一天'}沒有${kindLabel}沖帳紀錄。`}
      footer={['合計', null, null, null, null, null, formatMoney(q.data?.totalSettled ?? '0')]}
    />
  );
}

type OverviewReport = NonNullable<ReturnType<typeof useArOverview>['data']>;

function OverviewTable({ q }: { q: Q<OverviewReport> }): JSX.Element {
  type Row = OverviewReport['rows'][number];
  const columns: Array<ReportColumn<Row>> = [
    { key: 'customerCode', label: '編號', value: (r) => r.customerCode ?? '', className: 'font-mono' },
    { key: 'customerName', label: '客戶', value: (r) => r.customerName },
    {
      key: 'openingBalance',
      label: '期初未結',
      align: 'right',
      value: (r) => Number(r.openingBalance),
      render: (r) => formatMoney(r.openingBalance),
    },
    {
      key: 'periodSales',
      label: '本期出貨',
      align: 'right',
      value: (r) => Number(r.periodSales),
      render: (r) => formatMoney(r.periodSales),
    },
    {
      key: 'periodAllowance',
      label: '本期折讓',
      align: 'right',
      value: (r) => Number(r.periodAllowance),
      render: (r) => formatMoney(r.periodAllowance),
    },
    {
      key: 'periodPayments',
      label: '本期收款',
      align: 'right',
      value: (r) => Number(r.periodPayments),
      render: (r) => formatMoney(r.periodPayments),
    },
    {
      key: 'closingBalance',
      label: '期末未結',
      align: 'right',
      value: (r) => Number(r.closingBalance),
      render: (r) => formatMoney(r.closingBalance),
    },
  ];

  return (
    <ReportTable
      columns={columns}
      rows={q.data?.rows ?? []}
      rowKey={(r) => r.customerId}
      isLoading={q.isLoading}
      isError={q.isError}
      error={q.error}
      emptyHint="這個區間內沒有任何客戶有應收往來。"
      footer={[
        '總計',
        null,
        formatMoney(q.data?.totalOpening ?? '0'),
        formatMoney(q.data?.totalSales ?? '0'),
        formatMoney(q.data?.totalAllowance ?? '0'),
        formatMoney(q.data?.totalPayments ?? '0'),
        formatMoney(q.data?.totalClosing ?? '0'),
      ]}
    />
  );
}

type AgingReportData = NonNullable<ReturnType<typeof useArAging>['data']>;

function AgingTable({ q }: { q: Q<AgingReportData> }): JSX.Element {
  type Row = AgingReportData['rows'][number];
  const columns: Array<ReportColumn<Row>> = [
    { key: 'customerCode', label: '編號', value: (r) => r.customerCode ?? '', className: 'font-mono' },
    { key: 'customerName', label: '客戶', value: (r) => r.customerName },
    {
      key: 'notDue',
      label: '未到期',
      align: 'right',
      value: (r) => Number(r.notDue),
      render: (r) => formatMoney(r.notDue),
    },
    {
      key: 'days1to30',
      label: '1-30 天',
      align: 'right',
      value: (r) => Number(r.days1to30),
      render: (r) => formatMoney(r.days1to30),
    },
    {
      key: 'days31to60',
      label: '31-60 天',
      align: 'right',
      value: (r) => Number(r.days31to60),
      render: (r) => formatMoney(r.days31to60),
    },
    {
      key: 'days61to90',
      label: '61-90 天',
      align: 'right',
      value: (r) => Number(r.days61to90),
      render: (r) => formatMoney(r.days61to90),
    },
    {
      key: 'over90',
      label: '90 天以上',
      align: 'right',
      value: (r) => Number(r.over90),
      render: (r) => formatMoney(r.over90),
    },
    {
      key: 'total',
      label: '未結合計',
      align: 'right',
      value: (r) => Number(r.total),
      render: (r) => formatMoney(r.total),
    },
  ];

  return (
    <div className="grid gap-2">
      {q.data ? (
        <p className="text-xs text-slate-500">
          基準日 {q.data.asOf}(台北)。逾期天數以到期日計算,沒有到期日的單改用單據日期。
        </p>
      ) : null}
      <ReportTable
        columns={columns}
        rows={q.data?.rows ?? []}
        rowKey={(r) => r.customerId}
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        emptyHint="目前沒有未結的應收款項。"
        footer={[
          '總計',
          null,
          formatMoney(q.data?.totalNotDue ?? '0'),
          formatMoney(q.data?.totalDays1to30 ?? '0'),
          formatMoney(q.data?.totalDays31to60 ?? '0'),
          formatMoney(q.data?.totalDays61to90 ?? '0'),
          formatMoney(q.data?.totalOver90 ?? '0'),
          formatMoney(q.data?.totalOutstanding ?? '0'),
        ]}
      />
    </div>
  );
}

type LabelReport = NonNullable<ReturnType<typeof useArMailingLabels>['data']>;

/**
 * 郵寄標籤 —— 唯一不是表格的一張。
 *
 * **沒有引進標籤 / PDF 套件**:標籤就是一格格的方塊,`@media print` 加
 * `break-inside: avoid` 就不會被切半,瀏覽器 `Cmd+P` 直接印。
 * 與月結對帳單同一個取捨(REQ-20260806002 §3.4)。
 */
function MailingLabels({ q }: { q: Q<LabelReport> }): JSX.Element {
  if (q.isLoading) {
    return (
      <div role="status" className="text-slate-500 text-sm">
        載入中...
      </div>
    );
  }
  if (q.isError) {
    return (
      <div role="alert" className="text-red-600 text-sm">
        載入失敗：{q.error instanceof Error ? q.error.message : '未知錯誤'}
      </div>
    );
  }
  const rows = q.data?.rows ?? [];
  if (rows.length === 0) {
    return <div className="text-slate-500 text-sm">目前沒有還欠款的客戶,不需要印標籤。</div>;
  }

  return (
    <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid="mailing-labels">
      {rows.map((r) => (
        <MailingLabel key={r.customerId} row={r} />
      ))}
    </div>
  );
}

function MailingLabel({ row }: { row: MailingLabelRow }): JSX.Element {
  return (
    <div className="print-label border rounded-md p-3 bg-white text-sm leading-6">
      <div className="font-medium">{row.customerName}</div>
      {row.customerCode ? (
        <div className="text-xs text-slate-400 font-mono">{row.customerCode}</div>
      ) : null}
      <div>{row.address ?? '(未填地址)'}</div>
      <div className="text-slate-600 text-xs">
        {row.contact ? `${row.contact} ` : ''}
        {row.phone ?? row.mobile ?? ''}
      </div>
      {row.taxId ? <div className="text-slate-600 text-xs">統編 {row.taxId}</div> : null}
      <div className="text-xs text-slate-500 mt-1">
        未結 <span className="font-mono">{formatMoney(row.outstanding)}</span>
      </div>
    </div>
  );
}

/** 日報表預設今天(台北)—— 容器與瀏覽器時區都不一定是台北,明確指定 */
function todayInTaipei(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Asia/Taipei',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}
